import React, { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import { useStyles, PaginationInput } from '../pages/styles';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { IPageInfo } from '../models/page.types';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import TooltipOne from './TooltipOne/TooltipOne.component';
import { useTranslation } from 'react-i18next';
import { FormLabel } from '@material-ui/core';
import { useTabNavigation } from '../Context/TabNavigation.context';
import UnavailableIcon from './EditSession/UnavailableIcon.component';


interface PaginationProps {
  pageInfo?: IPageInfo;
  getPage?: (_page?: number, _newPageSize?: number) => void;
  className?: string;
  filterActive?: boolean;
  recordsFound?: number;
  isLoader?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({ pageInfo, getPage, className, recordsFound, filterActive, isLoader }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const contenerRef = useRef<HTMLDivElement | null>(null);

  const { tabIndexForChildren } = useTabNavigation(contenerRef.current);

  const [pageSize, setPageSize] = useState<number | undefined>(pageInfo?.pageSize);

  useEffect(() => {
    if (pageInfo?.pageSize !== undefined) {
      setPageSize(pageInfo?.pageSize);
    }
  }, [pageInfo?.pageSize]);

  function getPreviousUsersPage() {
    if (isLoader) return;
    if (pageInfo?.hasPrevious && pageInfo.currentPage >= 1) {
      getPage && getPage(pageInfo.currentPage - 1, pageSize);
    }
  }

  function getNextUsersPage() {
    if (isLoader) return;
    if (pageInfo?.hasNext && pageInfo.currentPage < pageInfo.totalPages) {
      getPage && getPage(pageInfo.currentPage + 1, pageSize);
    }
  }

  function getLastUsersPage() {
    if (isLoader) return;
    getPage && getPage(pageInfo?.totalPages ? pageInfo?.totalPages - 1 : 0, pageSize);
  }

  // handle pagination
  const handlePageSizeChange = (event: any) => {
    if (isLoader) return;
    getPage && getPage(0, event.target.value);
  };

  function getPaginationLabel() {
    if (pageInfo) {
      if (recordsFound && filterActive && recordsFound !== pageInfo.totalCount) {
        return !pageInfo.totalCount
          ? `Znaleziono 0 rekordów`
          : `Znaleziono ${pageInfo.totalCount} z ${recordsFound} rekordów`;
      } else {
        const lastVisibleIndex = (pageInfo.currentPage + 1) * pageInfo.pageSize;
        const to = lastVisibleIndex > pageInfo.totalCount ? pageInfo.totalCount : lastVisibleIndex;
        const from = to === 0 ? 0 : pageInfo.currentPage * pageInfo.pageSize + 1;
        return (
          <>
            {from}-{to} z {pageInfo.totalCount}
          </>
        );
      }
    }
  }

  const goToFirst = () => {
    if (pageInfo?.hasPrevious && pageInfo.currentPage >= 1) {
      getPage && getPage(0);
    } else return;
  };
  const goToLast = () => {
    if (pageInfo?.hasNext) {
      getLastUsersPage();
    } else return;
  };

  const handleKeyDownPagination = useCallback(
    (e) => {
      const { key } = e;
      switch (key) {
        case 'ArrowRight':
          if (e.metaKey || e.ctrlKey) {
            getNextUsersPage();
          }
          break;
        case 'ArrowLeft':
          if (e.metaKey || e.ctrlKey) {
            getPreviousUsersPage();
          }
          break;
        default:
          break;
      }
    },
    [pageInfo],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDownPagination);

    return () => {
      window.removeEventListener('keydown', handleKeyDownPagination);
    };
  }, [handleKeyDownPagination]);


  return (
    <div
      ref={contenerRef}
      tabIndex={0}
      className={className ? [classes.pagination, className].join(' ') : classes.pagination}
    >
      <Typography className={classes.paginationText}>{getPaginationLabel()}</Typography>
      <TooltipOne title="Pierwsza" placement="top">
        <div
          className={classes.paginationIconWrapper}
          tabIndex={tabIndexForChildren}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              goToFirst();
            }
          }}
        >
          <UnavailableIcon isVisible={!pageInfo?.hasPrevious} styles={{
            position: 'absolute',
            height: '33%',
            width: '33%',
            left: '0px',
            top: '2px'
          }} />
          <FirstPageIcon className={classes.paginationIcon} onClick={goToFirst} style={{
            cursor: !pageInfo?.hasPrevious ? "not-allowed" : 'pointer',
          }} />
        </div>
      </TooltipOne>
      <TooltipOne title="Poprzednia" placement="top">
        <div
          className={classes.paginationIconWrapper}
          tabIndex={tabIndexForChildren}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              getPreviousUsersPage();
            }
          }}
        >
          <UnavailableIcon isVisible={!pageInfo?.hasPrevious} styles={{
            position: 'absolute',
            height: '33%',
            width: '33%',
            left: '18px',
            top: '2px'
          }} />
          <NavigateBeforeIcon
            className={classes.paginationIcon}
            onClick={() => {
              getPreviousUsersPage();
            }}
            style={{
              cursor: !pageInfo?.hasPrevious ? "not-allowed" : 'pointer',
            }}
          />
        </div>
      </TooltipOne>
      <TooltipOne title="Następna" placement="top">
        <div
          className={classes.paginationIconWrapper}
          tabIndex={tabIndexForChildren}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              getNextUsersPage();
            }
          }}
        >
          <UnavailableIcon isVisible={!pageInfo?.hasNext} styles={{
            position: 'absolute',
            height: '33%',
            width: '33%',
            left: '38px',
            top: '2px'
          }} />
          <NavigateNextIcon
            className={classes.paginationIcon}
            onClick={() => {
              getNextUsersPage();
            }}
            style={{
              cursor: !pageInfo?.hasNext ? "not-allowed" : 'pointer',
            }}
          />
        </div>
      </TooltipOne>
      <TooltipOne title="Ostatnia" placement="top">
        <div
          className={classes.paginationIconWrapper}
          tabIndex={tabIndexForChildren}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              goToLast();
            }
          }}
        >
          <UnavailableIcon isVisible={!pageInfo?.hasNext} styles={{
            position: 'absolute',
            height: '33%',
            width: '33%',
            top: '2px',
            left: '60px'
          }} />

          <LastPageIcon className={classes.paginationIcon} onClick={goToLast} style={{
            cursor: !pageInfo?.hasNext ? "not-allowed" : 'pointer',
          }} />
        </div>
      </TooltipOne>
      <Typography className={classes.paginationLabel}>Wyświetl</Typography>
      <FormLabel className={classes.visuallyHidden} htmlFor="pagesize">Wielkość strony</FormLabel>

      <FormControl className={classes.margin}>
        <Select
          id='pagesize'
          value={pageSize === undefined ? 20 : pageSize}
          onChange={handlePageSizeChange}
          input={<PaginationInput id='pagesize'/>}
          inputProps={{ 'aria-hidden': 'false', 'aria-label': 'wielkość strony' }}
        >
          <MenuItem disableRipple value={20}>
            20
          </MenuItem>
          <MenuItem disableRipple value={50}>
            50
          </MenuItem>
          <MenuItem disableRipple value={100}>
            100
          </MenuItem>
          <MenuItem disableRipple value={200}>
            200
          </MenuItem>
          <MenuItem disableRipple value={500}>
            500
          </MenuItem>
        </Select>
      </FormControl>
    </div>
  );
};

export default Pagination;
